import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  SCALE_STATS,
  SECTORS,
  TICKER_BRAND_LOGOS,
  TICKER_MEDIA,
  TICKER_LANGUAGES,
} from "../data/content";
import useInView from "../hooks/useInView";
import { Tag } from "./ui-primitives";

const TICKER_DATA = {
  sectors: SECTORS,
  brands: TICKER_BRAND_LOGOS,
  media: TICKER_MEDIA,
  languages: TICKER_LANGUAGES,
};

export default function HeroStatsTabsV2({ theme = "light" }) {
  // Default the "By the numbers" panel to the BRANDS tab — visitors land
  // straight on the logo wall, which is the strongest social-proof artefact
  // on the page. Ads Placed / Media Partners / Languages remain one click away.
  const [activeIdx, setActiveIdx] = useState(1);
  const total = SCALE_STATS.length;
  const active = SCALE_STATS[activeIdx];
  const items = TICKER_DATA[active.tickerKey] || [];

  const scrollerRef = useRef(null);
  const tabRefs = useRef([]);
  const snapTimeoutRef = useRef(null);

  const scrollActiveIntoView = (idx) => {
    const container = scrollerRef.current;
    const el = tabRefs.current[idx];
    if (!container || !el) return;
    // Compute the tab's position inside the scroller's content origin.
    // (Don't rely on el.offsetLeft — the scroller is not the offsetParent.)
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const tabPos = eRect.left - cRect.left + container.scrollLeft;
    // Offset so the previous tab peeks on the left (helps users orient
    // themselves and discover the scroll direction).
    const PEEK = 56;
    const target = Math.max(
      0,
      Math.min(tabPos - PEEK, container.scrollWidth - container.clientWidth)
    );
    try {
      container.scrollTo({ left: target, behavior: "smooth" });
    } catch {
      container.scrollLeft = target;
    }
    // Hard-snap after the smooth animation finishes — defeats sub-pixel rounding
    // on Android Chrome that left tab 0 visually clipped after wrap-around.
    if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    snapTimeoutRef.current = setTimeout(() => {
      if (scrollerRef.current) scrollerRef.current.scrollLeft = target;
    }, 480);
  };

  const selectTab = (idx) => {
    setActiveIdx(idx);
    requestAnimationFrame(() => scrollActiveIntoView(idx));
  };

  // Only scroll-into-view on user clicks (handled by selectTab).
  // Skipping the previous useEffect that auto-scrolled on initial mount
  // means the strip always renders from idx 0 (₹200+ Cr) on the left edge,
  // even when a non-zero default tab (BRANDS) is active. Active styling
  // still shows the correct selection — the strip just doesn't auto-scroll.

  const isLight = theme === "light";
  const fg = isLight ? "var(--color-on-surface)" : "var(--color-on-primary)";
  const faint = isLight ? "rgba(4,13,35,0.45)" : "rgba(249,249,248,0.45)";
  const lineFaint = isLight
    ? "1px solid var(--color-outline-variant)"
    : "1px solid rgba(249,249,248,0.08)";
  const lineSep = isLight
    ? "1px solid var(--color-outline)"
    : "1px solid rgba(249,249,248,0.1)";

  return (
    <div data-testid="hero-stats-tabs">
      {/* Header — red dot + caps label only.
          No arrows / counter: the dots below the ticker fully express
          position AND act as quick-jump targets, and the tabs/swipe are
          the primary interaction. Less chrome, clearer story. */}
      <div className="flex items-center mb-6">
        <Tag light={!isLight}>By the Numbers</Tag>
      </div>

      {/* Tab row — horizontal scroll on mobile, 4-col grid on desktop.
          Wrapped in a relative container so we can render a right-edge fade
          gradient (hint that the row is swipable). */}
      <div className="stat-tab-scroller-wrap" style={{ position: "relative" }}>
        <div
          ref={scrollerRef}
          className="hide-scrollbar flex overflow-x-auto lg:grid lg:grid-cols-4 lg:overflow-visible"
          style={{
            borderBottom: lineFaint,
            WebkitOverflowScrolling: "touch",
            scrollBehavior: "smooth",
          }}
        >
          {SCALE_STATS.map((s, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={s.label}
                ref={(el) => (tabRefs.current[i] = el)}
                type="button"
                data-testid={`hero-stat-tab-${s.tickerKey}`}
                onClick={() => selectTab(i)}
                aria-pressed={isActive}
                className="text-left py-5 lg:py-6 transition-opacity stat-tab-btn"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  paddingLeft: i === 0 ? 0 : 20,
                  paddingRight: 20,
                  borderLeft: i === 0 ? "none" : lineSep,
                  position: "relative",
                  opacity: isActive ? 1 : 0.7,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.opacity = "0.7";
                }}
              >
                <div
                  className="tabular flex items-baseline gap-2"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "clamp(1.625rem, 3.2vw, 2.25rem)",
                    fontWeight: 800,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: fg,
                    marginBottom: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>
                    {s.value}
                    {s.suffix}
                  </span>
                  {isActive && (
                    <span
                      aria-hidden
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "var(--color-tertiary)",
                        display: "inline-block",
                      }}
                    />
                  )}
                </div>
                <div
                  className="label-sm"
                  style={{
                    color: isActive ? (isLight ? "var(--color-on-surface)" : "rgba(249,249,248,0.85)") : faint,
                    fontWeight: isActive ? 600 : 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.label}
                </div>

                {/* Underline — active = wide solid red; inactive = pulsing red hint.
                    Pulse runs at high opacity so the affordance is clearly visible
                    while still signalling motion/interactivity. */}
                {isActive ? (
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      bottom: -1,
                      left: i === 0 ? 0 : 20,
                      width: 40,
                      height: 3,
                      background: "var(--color-tertiary)",
                      transition: "width 0.25s ease, height 0.25s ease",
                    }}
                  />
                ) : (
                  <span
                    aria-hidden
                    className="tab-hint-pulse"
                    style={{
                      position: "absolute",
                      bottom: -1,
                      left: i === 0 ? 0 : 20,
                      height: 2,
                      background: "var(--color-tertiary)",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
        {/* Right-edge fade gradient — hints there's more content to scroll on mobile.
            Hidden on lg+ where all 4 tabs fit in a grid. */}
        <div
          aria-hidden
          className="stat-tab-scroll-hint lg:hidden"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 1,
            width: 56,
            pointerEvents: "none",
            background: isLight
              ? "linear-gradient(to right, rgba(245,243,239,0) 0%, rgba(245,243,239,0.95) 100%)"
              : "linear-gradient(to right, rgba(4,13,35,0) 0%, rgba(4,13,35,0.95) 100%)",
          }}
        />
      </div>

      {/* Ticker — full-bleed, breaks out of the parent .di-container so it
          visually matches the WHAT WE DO ticker (viewport-edge to viewport-edge). */}
      <div className="ticker-fullbleed">
        <Ticker tickerKey={active.tickerKey} items={items} isLight={isLight} theme={theme} />
      </div>

      {/* Mobile dots — placed BELOW the ticker so they read as part of the
          tab group's continuation, not as a break between tabs and ticker. */}
      <div className="flex lg:hidden items-center justify-center gap-2 pt-5">
        {SCALE_STATS.map((s, i) => (
          <button
            key={s.label}
            type="button"
            onClick={() => selectTab(i)}
            data-testid={`hero-stat-dot-${i}`}
            aria-label={`Stat ${i + 1}`}
            style={{
              width: i === activeIdx ? 22 : 6,
              height: 6,
              borderRadius: 3,
              background:
                i === activeIdx ? "var(--color-tertiary)" : isLight ? "rgba(4,13,35,0.18)" : "rgba(249,249,248,0.2)",
              border: "none",
              cursor: "pointer",
              transition: "width 0.3s ease, background-color 0.3s ease",
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes heroTabIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInTicker { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes tabHint {
          0%, 100% { opacity: 0.65; width: 20px; }
          50%      { opacity: 1;    width: 32px; }
        }
        .tab-hint-pulse { animation: tabHint 2.4s ease-in-out infinite; width: 20px; }
        .hide-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }

        /* Mobile tab sizing — ~2.2 tabs visible at once */
        .stat-tab-btn { flex: 0 0 44%; max-width: unset; }
        .stat-tab-number { font-size: clamp(2rem, 7vw, 2.5rem); }
        @media (min-width: 640px) {
          .stat-tab-btn { flex: 0 0 32%; }
        }
        @media (min-width: 1024px) {
          .stat-tab-btn { flex: unset; }
          .stat-tab-number { font-size: clamp(1.5rem, 3.2vw, 2.25rem); }
        }
      `}</style>
    </div>
  );
}

function Ticker({ tickerKey, items, isLight, theme }) {
  const fg = isLight ? "var(--color-on-surface)" : "var(--color-on-primary)";
  const muted = isLight ? "rgba(4,13,35,0.55)" : "rgba(249,249,248,0.55)";
  const text = isLight ? "rgba(4,13,35,0.92)" : "rgba(249,249,248,0.92)";
  const [rootRef, inView] = useInView();

  // Memoise the marquee item tree — flatMap creates 50-100 elements per
  // render. Without memo, every parent re-render (e.g. when inView flips)
  // rebuilds the entire list. Deps cover every value referenced inside.
  const tickerItems = useMemo(() => {
    const copies = items.length >= 8 ? 2 : 4;
    return Array.from({ length: copies }).flatMap((_, copyIdx) =>
      items.map((it, i) => {
        const k = `${copyIdx}-${i}`;
        if (tickerKey === "languages") {
          return (
            <div key={k} className="flex items-baseline gap-3 whitespace-nowrap">
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(1.5rem, 2.8vw, 2rem)",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: fg,
                }}
              >
                {it.native}
              </span>
              <span
                className="label-sm"
                style={{
                  color: muted,
                  fontWeight: 500,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                {it.english !== it.native ? it.english : ""}
              </span>
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: "var(--color-tertiary)",
                  display: "inline-block",
                }}
              />
            </div>
          );
        }
        return (
          <div key={k} className="flex items-center gap-4 whitespace-nowrap">
            {it && typeof it === "object" && it.src ? (
              <img
                src={it.src}
                alt={it.title}
                title={it.title}
                loading="lazy"
                draggable={false}
                style={{
                  height: "clamp(28px, 3.2vw, 44px)",
                  width: "auto",
                  maxWidth: 200,
                  objectFit: "contain",
                  filter:
                    theme === "dark"
                      ? "brightness(0) invert(1)"
                      : "none",
                  userSelect: "none",
                }}
              />
            ) : (
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(1.25rem, 2.4vw, 1.75rem)",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: text,
                }}
              >
                {it}
              </span>
            )}
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: "var(--color-tertiary)",
                display: "inline-block",
              }}
            />
          </div>
        );
      })
    );
  }, [tickerKey, items, fg, muted, text, theme]);

  return (
    <div
      ref={rootRef}
      key={tickerKey}
      data-testid={`hero-ticker-${tickerKey}`}
      style={{
        overflow: "hidden",
        paddingTop: 28,
        paddingBottom: 8,
        animation: "fadeInTicker 0.45s ease both",
      }}
    >
      <div
        className="flex items-center marquee-track"
        style={{
          width: "max-content",
          gap: 32,
          animationPlayState: inView ? "running" : "paused",
        }}
      >
        {tickerItems}
      </div>
    </div>
  );
}
